// 令牌存放：refresh 只在 httpOnly cookie 里（页面脚本碰不到，XSS 拿不走长期凭证）；access 放 localStorage，跨标签共享，15 分钟过期。
// refresh 是一次性的（复用会被服务端判为盗用并吊销全部会话），所以刷新必须跨标签互斥：
// 用 Web Locks 串行化，锁内先看别的标签是否已经刷新过。

export type Tokens = { access: string; expiresAt: number };

const KEY = "pb.tokens";
/** access 剩余不足这么多毫秒就提前刷新 */
export const REFRESH_SKEW_MS = 30_000;

export function loadTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as Tokens & { refresh?: unknown };
    // 旧版把 refresh 放在这里：清掉，重新登录一次换成 cookie
    if ("refresh" in t) {
      localStorage.removeItem(KEY);
      return null;
    }
    return typeof t.access === "string" ? t : null;
  } catch {
    return null;
  }
}

export function saveTokens(t: Tokens | null) {
  try {
    if (t) localStorage.setItem(KEY, JSON.stringify(t));
    else localStorage.removeItem(KEY);
  } catch {
    // 隐私模式等：只在内存里有效
  }
}

export function onTokensChangedElsewhere(cb: (t: Tokens | null) => void): () => void {
  const h = (e: StorageEvent) => {
    if (e.key === KEY) cb(loadTokens());
  };
  window.addEventListener("storage", h);
  return () => window.removeEventListener("storage", h);
}

let localChain: Promise<unknown> = Promise.resolve();

/** 跨标签互斥执行（不支持 Web Locks 时退化为本标签内串行）。 */
export function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  if (locks?.request) return locks.request("pb-refresh", fn) as Promise<T>;
  const run = localChain.then(fn, fn);
  localChain = run.catch(() => undefined);
  return run;
}
